import React, { useState } from "react"
import { ButtonBase, IconButton, Theme, Tooltip, Typography, makeStyles } from "@material-ui/core"
import { ChevronDown, ChevronUp, X } from "react-feather"
import { useTranslation } from "react-i18next"
import { MessageViewModel } from "../api/message"

type Props = {
  messages: MessageViewModel[]
  onJumpToMessage: (messageId: string) => void
  onUnpin: (messageId: string) => void
}

const PinnedMessagesBar = ({ messages, onJumpToMessage, onUnpin }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")

  const [expanded, setExpanded] = useState(false)

  if (messages.length === 0) return null

  const visible = expanded ? messages : messages.slice(0, 1)

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          <span aria-hidden>📌</span>{" "}
          {messages.length === 1
            ? t("pinnedMessagesBar.singleTitle")
            : t("pinnedMessagesBar.multipleTitle", { count: messages.length })}
        </Typography>

        {messages.length > 1 && (
          <IconButton
            size="small"
            aria-label={
              expanded ? t("pinnedMessagesBar.collapseButtonLabel") : t("pinnedMessagesBar.expandButtonLabel")
            }
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </IconButton>
        )}
      </div>

      {visible.map((message) => (
        <div key={message.id} className={classes.item}>
          <ButtonBase className={classes.preview} onClick={() => onJumpToMessage(message.id)}>
            <Typography className={classes.sender}>{message.senderName}</Typography>

            <Typography className={classes.content}>{message.content || t("attachmentLabel")}</Typography>
          </ButtonBase>

          <Tooltip title={t("unpinLabel")}>
            <IconButton size="small" aria-label={t("unpinLabel")} onClick={() => onUnpin(message.id)}>
              <X size={14} />
            </IconButton>
          </Tooltip>
        </div>
      ))}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  title: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },

  item: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  preview: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
    borderRadius: 6,

    "&:hover": {
      background: theme.palette.action.hover,
    },
  },

  sender: {
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
  },

  content: {
    minWidth: 0,
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}))

export default PinnedMessagesBar
